import { FunctionComponent, ReactNode, useCallback, useEffect } from "react";
import { ThemedLayoutV2 } from "@refinedev/mui";
import { useKbarActions } from "../../hooks/useKbarActions";
import { ThemedKbar } from "./ThemedKbar";
import {
  Header,
  HeaderWithTitle,
  HeaderWithTitleAndMarketplace,
} from "./Header";
import { TitleProps } from "@refinedev/core";
import { useInitializeApp } from "../../hooks/useInitializeApp";
import { useAdminAccess } from "../../hooks/useAdminAccess";
import { getAdminHome } from "../../utils/adminAccess";
import { Sider } from "./Sider";
import { AdminLayout } from "./admin/AdminLayout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReAuthentication } from "../../pages/authentication/ReAuthentication";
import { ReAuthenticationCallback } from "../../pages/authentication/ReAuthenticationCallback";
import { SyncrosaleThemedTitle } from "./SyncrosaleThemedTitle";
import { SyncrosaleLoader } from "./components/SyncrosaleLoader";
import { FinanceAuthorizationAlert } from "./FinanceAuthorizationAlert";
import { getRuntimeEnv } from "../../providers/getRuntimeEnv";

interface SyncrosaleLayoutProps {
  children: ReactNode;
}

// useRefineKbar bilerek çağrılmıyor: resource'lardan otomatik üretilen
// "... listele" aksiyonları yerine useKbarActions'taki özel aksiyonlar kullanılıyor.
const KbarSetup = () => {
  useKbarActions();
  return null;
};

export const SyncrosaleLayout = ({ children }: SyncrosaleLayoutProps) => {
  const { showLoading, hasStore, needsReAuthentication, invalidMarketplace } =
    useInitializeApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const adminAccess = useAdminAccess();

  // An admin without any marketplace/store has nothing to set up as a seller:
  // they get the admin pages instead of the "create store" flow.
  const isAdminOnly = adminAccess.isAdmin && !hasStore;

  const runtimeEnv = getRuntimeEnv();
  const isDevEnv = runtimeEnv === "dev";
  const title = isDevEnv ? "SyncroDev" : "Syncrosale";
  const logo = isDevEnv ? "/icons/logo-dev.svg" : "/icons/logo.svg";

  const renderTitle: FunctionComponent<TitleProps> = useCallback(
    (props) => <SyncrosaleThemedTitle {...props} text={title} icon={logo} />,
    [title, logo],
  );

  const isReAuthCallback =
    searchParams.get("spapi_oauth_code") &&
    localStorage.getItem("reAuthenticating") === "true";

  useEffect(() => {
    if (
      !hasStore &&
      !adminAccess.isAdmin &&
      !showLoading &&
      !needsReAuthentication &&
      window.location.pathname !== "/store/create" &&
      !isReAuthCallback
    ) {
      setTimeout(() => navigate("/store/create"), 100);
    }
  }, [
    hasStore,
    adminAccess.isAdmin,
    navigate,
    showLoading,
    needsReAuthentication,
    isReAuthCallback,
  ]);

  const isOnAdminPage = window.location.pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdminOnly && !showLoading && !isOnAdminPage) {
      navigate(getAdminHome(adminAccess), { replace: true });
    }
  }, [isAdminOnly, showLoading, isOnAdminPage, navigate, adminAccess]);

  if (showLoading) {
    return <SyncrosaleLoader />;
  }

  if (isReAuthCallback) {
    return <ReAuthenticationCallback />;
  }

  if (isAdminOnly) {
    // Seller pages (including /store/create) have no meaning here; wait for
    // the redirect above instead of flashing them.
    return isOnAdminPage ? (
      <AdminLayout>{children}</AdminLayout>
    ) : (
      <SyncrosaleLoader />
    );
  }

  if (window.location.pathname === "/store/create") {
    return (
      <ThemedLayoutV2
        Header={HeaderWithTitleAndMarketplace}
        Title={renderTitle}
        Sider={() => null}
      >
        {children}
      </ThemedLayoutV2>
    );
  }

  if (needsReAuthentication && invalidMarketplace) {
    return (
      <ThemedLayoutV2
        Header={HeaderWithTitleAndMarketplace}
        Title={renderTitle}
        Sider={() => null}
      >
        <ReAuthentication invalidMarketplaces={[invalidMarketplace]} />
      </ThemedLayoutV2>
    );
  }

  return (
    <>
      <ThemedKbar />
      <KbarSetup />
      <ThemedLayoutV2
        Header={hasStore ? Header : HeaderWithTitle}
        Title={renderTitle}
        Sider={hasStore ? Sider : () => null}
      >
        {hasStore && <FinanceAuthorizationAlert />}
        {children}
      </ThemedLayoutV2>
    </>
  );
};
